import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, CheckCircle2, Timer, ArrowRight, Check, GitCompare } from "lucide-react";
import { DynIcon } from "@/components/DynIcon";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ECOSYSTEM_NODES } from "@/lib/mockData";

const CENTER = 50;

export const EcosystemCore = ({ className = "" }) => {
  const [active, setActive] = useState("ecom");
  const [detail, setDetail] = useState(null);
  const [compareKey, setCompareKey] = useState(null);
  const node = ECOSYSTEM_NODES.find((n) => n.key === active);
  const open = ECOSYSTEM_NODES.find((n) => n.key === detail);
  const compare = ECOSYSTEM_NODES.find((n) => n.key === compareKey);

  return (
    <div data-testid="ecosystem-core" className={className}>
      <div className="relative mx-auto aspect-square w-full max-w-[520px]">
        {/* connective lines */}
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {ECOSYSTEM_NODES.map((n) => (
            <line
              key={n.key}
              x1={CENTER}
              y1={CENTER}
              x2={n.x}
              y2={n.y}
              stroke="#CBD5E1"
              strokeWidth="0.35"
              strokeDasharray="1.2 1.2"
            />
          ))}
          <AnimatePresence mode="wait">
            <motion.line
              key={node.key}
              x1={CENTER}
              y1={CENTER}
              x2={node.x}
              y2={node.y}
              stroke="#4288CE"
              strokeWidth="0.8"
              strokeLinecap="round"
              style={{ filter: "drop-shadow(0 0 2px rgba(66,136,206,0.7))" }}
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.55, ease: "easeInOut" }}
            />
          </AnimatePresence>
        </svg>

        {/* central money core */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <span className="absolute -inset-10 rounded-full bg-brand-tint" />
          <span className="absolute -inset-10 rounded-full border border-brand/20 animate-ping" style={{ animationDuration: "3s" }} />
          <span className="absolute -inset-5 rounded-full border border-brand/30 animate-ping" style={{ animationDuration: "2.2s" }} />
          <div className="relative flex h-28 w-28 flex-col items-center justify-center rounded-full bg-brand text-white shadow-float-lg">
            <Activity className="h-6 w-6" aria-hidden="true" />
            <p className="mt-1 px-2 text-center text-[0.62rem] font-semibold leading-tight">
              Core Liquidity &amp; Money Engine
            </p>
          </div>
          <div className="absolute -bottom-9 left-1/2 w-max -translate-x-1/2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[0.65rem] font-semibold text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-blip" />
              100% Active Rails
            </span>
          </div>
        </div>

        {/* peripheral nodes */}
        {ECOSYSTEM_NODES.map((n) => {
          const on = n.key === active;
          return (
            <button
              key={n.key}
              data-testid={`eco-node-${n.key}`}
              aria-label={n.label}
              aria-pressed={on}
              onMouseEnter={() => setActive(n.key)}
              onFocus={() => setActive(n.key)}
              onClick={() => {
                setActive(n.key);
                setCompareKey(null);
                setDetail(n.key);
              }}
              className="absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${n.x}%`, top: `${n.y}%` }}
            >
              <motion.span
                whileHover={{ scale: 1.06 }}
                className={`flex w-[108px] flex-col items-center gap-1 rounded-2xl border bg-white px-2.5 py-2.5 text-center transition-all duration-200 ${
                  on ? "border-brand shadow-float" : "border-stroke shadow-sm hover:border-brand/40"
                }`}
              >
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-xl transition-colors ${
                    on ? "bg-brand text-white" : "bg-brand-tint text-brand"
                  }`}
                >
                  <DynIcon name={n.icon} className="h-4.5 w-4.5" />
                </span>
                <span className="text-[0.68rem] font-semibold leading-tight text-ink">{n.short}</span>
              </motion.span>
            </button>
          );
        })}
      </div>

      {/* readout */}
      <AnimatePresence mode="wait">
        <motion.div
          key={node.key}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25 }}
          className="mx-auto mt-6 max-w-md rounded-2xl border border-stroke bg-white p-4 shadow-sm"
          data-testid="eco-readout"
        >
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-tint text-brand">
              <DynIcon name={node.icon} className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-ink">{node.label}</p>
              <p className="text-xs text-slatey">{node.desc}</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 border-t border-stroke pt-3">
            <div className="flex items-center gap-2">
              <Timer className="h-4 w-4 text-brand" aria-hidden="true" />
              <div>
                <p className="text-sm font-bold text-ink tabular-nums">{node.settle}</p>
                <p className="text-[0.68rem] text-slatey">settlement time</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" aria-hidden="true" />
              <div>
                <p className="text-sm font-bold text-ink tabular-nums">{node.success}</p>
                <p className="text-[0.68rem] text-slatey">success rate</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              setCompareKey(null);
              setDetail(node.key);
            }}
            data-testid="eco-learn-more"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand transition-colors hover:text-brand-hover"
          >
            View coverage &amp; use cases
            <ArrowRight className="h-4 w-4" />
          </button>
        </motion.div>
      </AnimatePresence>

      {/* Node deep-dive + rail comparison */}
      <Dialog
        open={!!detail}
        onOpenChange={(o) => {
          if (!o) {
            setDetail(null);
            setCompareKey(null);
          }
        }}
      >
        <DialogContent className={compare ? "max-w-xl" : "max-w-md"} data-testid="eco-detail-dialog">
          {open && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand text-white">
                    <DynIcon name={open.icon} className="h-5 w-5" />
                  </span>
                  <div>
                    <DialogTitle className="text-left text-lg">{open.label}</DialogTitle>
                    <DialogDescription className="text-left">{open.desc}</DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              {/* Compare selector */}
              <div className="rounded-xl border border-stroke bg-canvas p-3" data-testid="eco-compare-picker">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-slatey">
                  <GitCompare className="h-3.5 w-3.5 text-brand" /> Compare with another rail
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {ECOSYSTEM_NODES.filter((n) => n.key !== open.key).map((n) => {
                    const on = compareKey === n.key;
                    return (
                      <button
                        key={n.key}
                        onClick={() => setCompareKey(on ? null : n.key)}
                        data-testid={`eco-compare-${n.key}`}
                        aria-pressed={on}
                        className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                          on ? "border-brand bg-brand text-white" : "border-stroke bg-white text-ink hover:border-brand/40"
                        }`}
                      >
                        <DynIcon name={n.icon} className="h-3.5 w-3.5" />
                        {n.short}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Comparison table */}
              <div className="overflow-hidden rounded-xl border border-stroke" data-testid="eco-compare-table">
                <div className={`grid ${compare ? "grid-cols-3" : "grid-cols-2"} border-b border-stroke bg-canvas`}>
                  <div className="px-3 py-2.5 text-[0.68rem] font-semibold uppercase tracking-wider text-slatey">Metric</div>
                  <div className="px-3 py-2.5 text-sm font-bold text-ink">{open.short}</div>
                  {compare && <div className="px-3 py-2.5 text-sm font-bold text-brand">{compare.short}</div>}
                </div>
                {[
                  { label: "Fees", a: open.fees, b: compare?.fees },
                  { label: "Settlement", a: open.settle, b: compare?.settle },
                  { label: "Success rate", a: open.success, b: compare?.success },
                  { label: "Coverage", a: open.coverage, b: compare?.coverage, wrap: true },
                ].map((row) => (
                  <div
                    key={row.label}
                    className={`grid ${compare ? "grid-cols-3" : "grid-cols-2"} border-b border-stroke/70 last:border-0`}
                  >
                    <div className="px-3 py-2.5 text-xs font-medium text-slatey">{row.label}</div>
                    <div className={`px-3 py-2.5 text-ink ${row.wrap ? "text-xs leading-relaxed" : "text-sm font-semibold tabular-nums"}`}>
                      {row.a}
                    </div>
                    {compare && (
                      <div className={`px-3 py-2.5 text-ink ${row.wrap ? "text-xs leading-relaxed" : "text-sm font-semibold tabular-nums"}`}>
                        {row.b}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {!compare && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slatey">Typical use cases</p>
                  <ul className="mt-3 grid grid-cols-2 gap-2.5">
                    {open.useCases.map((u) => (
                      <li key={u} className="flex items-center gap-2 text-sm text-ink">
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-tint">
                          <Check className="h-2.5 w-2.5 text-brand" />
                        </span>
                        {u}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
